import type { Emitter } from "@core/events/emitter";

/**
 * Selection-change event payload — lets observers do incremental UI updates
 * (e.g. only re-render rows whose id appeared in `addedIds` / `removedIds`).
 */
export interface SelectionChange {
  readonly selectedIds: ReadonlySet<string>;
  readonly addedIds: ReadonlyArray<string>;
  readonly removedIds: ReadonlyArray<string>;
  /** Most recently clicked id; mirrors Unity's `LastClicked`. */
  readonly primaryId: string | null;
  /**
   * The selection set this selection IS, or `null` when the ids were picked
   * individually. Mirrors `SelectionChangedArgs`' third member, which Unity
   * fills from `EditorModel.m_selectedSet` (`EditorModel.cs:553`).
   */
  readonly selectionSetId: string | null;
}

/** Modifier flags forwarded by the viewport / hierarchy click handlers. */
export interface ClickModifiers {
  /** Toggle this id in the selection (Shift in Unity, Cmd/Ctrl on Mac). */
  readonly multiselect?: boolean;
  /** Range-select between primary and clicked id. Currently same as
   *  `multiselect`; proper ordering arrives once the hierarchy publishes
   *  a flattened visible-row list. */
  readonly rangeselect?: boolean;
}

/**
 * `ISelectionModel` — port of Unity's `ISelectedGameObjectModel`
 * (`Models/SceneModel/SelectedGameObjectModel.cs`). The web port is
 * id-based (no Unity-object handles); ViewModels resolve ids through
 * `IProjectModel.getEntitySnapshotById` when they need transform / name.
 *
 * The rich per-entity properties (`Position`, `LocalEuler`, `LocalScale`,
 * visibility / lock, animation settings, isolation) landed with the M8
 * inspector slices and live in the per-context inspector models
 * (`PlacementModel`, `useInspector` sub-views) rather than here — this
 * model stays a pure id-based selection surface. Merge / un-merge — which
 * Unity also hangs off this model (`MergeParts` / `UnmergeParts`) — lives in
 * `IMergeModel` for the same reason: it writes snapshots, not selection. Still
 * unported: the drop-on-floor edit flags.
 */
export interface ISelectionModel {
  readonly onChanged: Emitter<SelectionChange>;
  /**
   * Unity `ISelectedGameObjectModel.NameChangeRequested`. The context menu only
   * ASKS to rename; the edit is an inline field on the hierarchy row
   * (`HierarchyViewModel.OnNameChangeRequested` sets `CanEditName`). Routing it
   * through the model rather than a component event is what lets the VIEWPORT
   * menu start an edit in the hierarchy panel, exactly as Unity does.
   */
  readonly onRenameRequested: Emitter<RenameRequest>;

  readonly selectedIds: ReadonlySet<string>;
  readonly primaryId: string | null;
  /**
   * Unity `IEditorModel.SelectedSelectionSet` (`IEditorModel.cs:292`), read
   * by `GameObjectContextViewModel` as `IsSelectionSet` to swap the inspector
   * into its selection-set state.
   *
   * STICKY UNTIL THE NEXT ORDINARY SELECTION CHANGE, which is Unity's own
   * semantic and not a convenience: `EditorModel` sets `m_selectedSet` in
   * `SelectSelectionSet` and clears it in the next `SelectionChanged` that did
   * not come from there (`EditorModel.cs:543-546`). So clicking a member row,
   * a viewport pick, or anything else that calls {@link click} / {@link set} /
   * {@link clear} drops back to the per-entity inspector.
   */
  readonly selectionSetId: string | null;

  isSelected(id: string): boolean;
  click(id: string, modifiers?: ClickModifiers): void;
  set(ids: Iterable<string>): void;
  /**
   * Select a selection SET: its member snapshots become the selection AND the
   * set is remembered as {@link selectionSetId}. Port of
   * `EditorModel.SelectSelectionSet` (`EditorModel.cs:772`).
   *
   * `memberIds` may be empty — a set whose entities were all removed still
   * exists (Unity: "selection set without entities is only 1 object, but has
   * to be treated as MultiSelection", `GameObjectContextViewModel.cs:980`), and
   * the inspector must still be able to rename or remove it.
   */
  setSelectionSet(selectionSetId: string, memberIds: Iterable<string>): void;
  clear(): void;
  /** Unity `RequestNameChange` — single snapshot, no write of its own. */
  requestRename(snapshotId: string): void;
}

export interface RenameRequest {
  readonly snapshotId: string;
}

export const SELECTION_MODEL = Symbol.for("Campfire.SelectionModel");
